import type { PolicyVersion } from '@main/pc-types';
import Slider from '@main/privacy-center/src/components/Slider';
import { moment } from '@main/utils';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { policyVersionSliderMessages } from './messages';
import {
  LabelsWrapper,
  PolicyVersionSliderWrapper,
  SliderLabel,
} from './wrappers';

/**
 * The PolicyVersionSlider props
 */
export interface PolicyVersionSliderProps {
  /** The different versions of the privacy policy, sorted least to most recent, minimum length of 2 */
  versions: PolicyVersion[];
  /** The default version index */
  defaultVersionInd?: number;
  /** Callback when selected version changes */
  onPolicyIndChange?: (policyInd: number) => void;
}

/**
 * The Page PolicyVersionSlider
 */
function PolicyVersionSlider({
  versions,
  defaultVersionInd = versions.length - 1,
  onPolicyIndChange,
}: PolicyVersionSliderProps): JSX.Element {
  const { formatMessage } = useIntl();
  const lastVersion = versions.length - 1;
  const [selectedVersionInd, setSelectedVersionInd] =
    useState(defaultVersionInd);

  /** Calculate and normalize the date diff between each policy version so that they are spaced wrt each other on the slider */
  const versionDaysDiff = versions.map((version) =>
    moment(version.createdAt).diff(moment(versions[0].createdAt), 'days'),
  );
  const last = versionDaysDiff[lastVersion];
  const versionDaysDiffCalibrated = versionDaysDiff.map(
    (diff) => (diff / last) * 100,
  );

  /**
   * Marks are in an object where the keys are the absolute value/location of where they are located along the slider
   * Each mark needs to be associated to a slider value but we don't want its label to be rendered, hence the map to empty string
   * {
   *  0: labelString,
   *  2: labelString,
   *  10: labelString,
   * }
   */
  const marks = versionDaysDiffCalibrated.reduce(
    (a, b) => ({ ...a, [b]: '' }),
    {},
  );

  /** This maps the absolute slider mark location to its raw index in the versions list */
  const marksToVersionInd: {
    [key: number]: number;
  } = versionDaysDiffCalibrated.reduce((a, b, i) => ({ ...a, [b]: i }), {});

  /** This maps the policy's raw index in the versions list to its absolute slider mark location */
  const versionIndToMarks: {
    [key: number]: number;
  } = versionDaysDiffCalibrated.reduce((a, b, i) => ({ ...a, [i]: b }), {});

  return (
    <PolicyVersionSliderWrapper>
      <LabelsWrapper>
        <SliderLabel selected={selectedVersionInd === 0}>
          {moment(versions[0].createdAt).format('MMMM YYYY')}
        </SliderLabel>
        {selectedVersionInd !== 0 && selectedVersionInd !== lastVersion && (
          <SliderLabel selected>
            {moment(versions[selectedVersionInd].createdAt).format(
              'MMMM D, YYYY',
            )}
          </SliderLabel>
        )}
        <SliderLabel selected={selectedVersionInd === lastVersion}>
          {formatMessage(policyVersionSliderMessages.current)}
        </SliderLabel>
      </LabelsWrapper>
      <Slider
        marks={marks}
        min={0}
        max={versionDaysDiffCalibrated[lastVersion]}
        defaultValue={versionIndToMarks[selectedVersionInd]}
        value={versionIndToMarks[selectedVersionInd]}
        step={null}
        onChange={(value) => {
          const markVal = Number(value);
          setSelectedVersionInd(marksToVersionInd[markVal]);
          if (onPolicyIndChange) {
            onPolicyIndChange(marksToVersionInd[markVal]);
          }
        }}
        tooltipVisible={false}
      />
    </PolicyVersionSliderWrapper>
  );
}

export default PolicyVersionSlider;
